import * as React from 'react';
import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { CustomerSearchParams } from './CustomerLookupForm';
import { Address, Customer, Pet } from './types';
import { Spinner } from '../assets/Spinner';
import { Pagination } from '../component/Pagination';
import { Table } from '../component/Table';
import { useFetchWithPagination } from '../hooks/useFetchWithPagination';
import { CustomerSearchHeaders } from '../tableHeaders';
import { formatDateTime, generateFullName, getAddressLines } from '../utils';

interface CustomerLookupResultsProps {
  onClick: () => void;
  searchParams: CustomerSearchParams;
}

export const CustomerLookupResults = ({ searchParams, onClick }: CustomerLookupResultsProps) => {
  const [, setUrlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    currentData: data,
    renderPagination,
    reFetch,
    searchBy,
    isLoading,
    ...forPagination
  } = useFetchWithPagination<Partial<Customer>>({
    endpoint: '/customer_search',
    payload: searchParams,
    mapper: (e) => e,
  });

  useEffect(() => {
    if (!isLoading && searchParams.order_id && data.length === 1) {
      const customer = data[0] as Partial<Customer>;
      const fullName = generateFullName(customer.first_name, customer.last_name, '_') ?? '';
      navigate(`/customer-lookup/${customer.id}__${fullName}/order/${searchParams.order_id}`, {
        replace: true,
      });
    }
  }, [isLoading, data, searchParams.order_id, navigate]);

  const handleAnotherSearch = () => {
    setUrlSearchParams({}, { replace: true });
    onClick();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="relative h-[calc(100vh-77px)] overflow-auto bg-slate50 p-8">
      <div className="mb-5">
        <h1>Customer Lookup: {forPagination.countOfResults} Results</h1>
      </div>

      <Table
        header={CustomerSearchHeaders}
        data={data.map((data: Partial<Customer> & { primary_address: Address }) => {
          return {
            Name: (
              <Link
                className="cursor-pointer underline text-anchor"
                to={`/customer-lookup/${data.id}__${
                  generateFullName(data.first_name, data.last_name, '_') ?? ''
                }`}
              >
                {generateFullName(data.first_name, data.last_name)}
              </Link>
            ),
            Email: data.email,
            Status: data.status ? data.status : '--',
            'Pet Names':
              data?.pets && data.pets.length > 0
                ? data.pets.map((pet: Partial<Pet>) => pet.name).join(', ')
                : '--',
            'Account Created': `${formatDateTime(data.created)}`,
            'Last Updated': `${formatDateTime(data.last_updated)}`,
            'Primary Shipping Address': <AddressRow address={data.primary_address} />,
          };
        })}
        internalPagination={false}
        tdClass="align-top"
      />
      {renderPagination && <Pagination {...forPagination} />}

      <div className="flex justify-center text-sm pt-3 pb-6">
        <a onClick={handleAnotherSearch} className="cursor-pointer underline text-anchor">
          Try another search
        </a>
      </div>
    </div>
  );
};

const AddressRow = ({ address }: { address: Address }) => {
  const addressLines = getAddressLines(address);
  return addressLines.map((addressLine, i) => (
    <p key={i} className="text-left">
      {addressLine}
    </p>
  ));
};
